import React, {useState} from 'react';

import {assert} from 'Asserts';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {AnalyticsDashboardSelectorQuery} from 'insights/analytics-dashboards/__generated/AnalyticsDashboardSelectorQuery';
import {
  AnalyticsDashboardActions,
  updateAnalyticsDashboard,
} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import AnalyticsDashboardSelector from 'insights/analytics-dashboards/AnalyticsDashboardSelector';
import {GraphQLAnalyticsDashboardSpec} from 'insights/analytics-dashboards/data/__generated/GraphQLAnalyticsDashboardSpec';
import * as MakeSpecRequest from 'insights/analytics-dashboards/data/MakeSpecRequest';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {GET_ANALYTICS_DASHBOARD_QUERY} from 'insights/analytics-dashboards/details/graphql/GetAnalyticsDashboard';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import navigateToURL from 'lib/navigateToURL';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

type SelectedDashboard =
  AnalyticsDashboardSelectorQuery['analyticsDashboardListing']['analyticsDashboards'][number];

export type Actions = Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'>;

export interface AddQueryToAnalyticsDashboardModalProps {
  actions: Actions;
  analyticsQuery: AnalyticsQuerySpecRequest;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function AddQueryToAnalyticsDashboardModal(
  props: AddQueryToAnalyticsDashboardModalProps
): JSX.Element {
  const [selectedDashboard, setSelectedDashboard] = useState<SelectedDashboard | null>(null);
  const canSubmit = selectedDashboard != null;

  async function handleSubmit(): Promise<void> {
    assert(selectedDashboard != null, 'Unexpected submit without dashboard selection');
    const updatedDashboard = await withLoadingScreen(
      (async () => {
        // Re-fetch the full dashboard spec so our update request contains the dashboard's current
        // parameter configs, layout rows, and other queries. This mirrors what the in-dashboard
        // "Add block" flow does, just from an external context.
        const data = await doQuerySafe<GetAnalyticsDashboard, {analyticsDashboardId: string}>(
          {query: GET_ANALYTICS_DASHBOARD_QUERY},
          {variables: {analyticsDashboardId: selectedDashboard.id}, fetchPolicy: 'network-only'}
        );
        const dashboardNode = data.analyticsDashboard;
        assert(
          dashboardNode != null && dashboardNode.__typename === 'AnalyticsDashboardNode',
          'Expected AnalyticsDashboardNode when fetching dashboard to add block'
        );
        return props.actions.updateAnalyticsDashboard(
          selectedDashboard.id,
          MakeSpecRequest.onAddAnalyticsQuery(
            dashboardNode as unknown as GraphQLAnalyticsDashboardSpec,
            props.analyticsQuery
          )
        );
      })(),
      'Adding block to dashboard...'
    );
    navigateToURL(updatedDashboard.url);
    props.onRequestSubmit();
  }

  return (
    <Modal
      buttons={[{disabled: !canSubmit, submit: true, text: 'Add block'}]}
      medium={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Add to dashboard"
    >
      <FormGroup>
        <FieldLabel label="Dashboard" />
        <AnalyticsDashboardSelector
          selectedAnalyticsDashboard={selectedDashboard}
          onSelect={setSelectedDashboard}
        />
      </FormGroup>
    </Modal>
  );
}

export default function ConnectedAddQueryToAnalyticsDashboardModal(
  props: Omit<AddQueryToAnalyticsDashboardModalProps, 'actions'>
): JSX.Element {
  return <AddQueryToAnalyticsDashboardModal {...props} actions={{updateAnalyticsDashboard}} />;
}
